import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import { useInputId } from './hooks';

function FormTextArea({
  id: defaultId,
  icon: Icon,
  label,
  name,
  onChange,
  onSetFieldValue,
  value,
  error,
  required,
  maxLength,
  withCounter,
  ...props
}) {
  const [id] = useInputId(defaultId);
  const handleChange = ({ target }) => {
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: target.value,
      }));
    }
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, target.value);
    }
  };
  return (
    <div className="flex flex-col">
      <label
        htmlFor={id}
        className="block text-xs font-medium mb-2 text-gray-500"
      >
        <span>{label}</span>
        {required && <span className="ml-1 font-light text-red-500">*</span>}
      </label>
      <div
        className={cn(
          'relative border rounded-2xl bg-white px-3 py-2 shadow-sm focus-within:ring-1 z-10',
          error
            ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500'
            : 'border-gray-300 focus-within:ring-primary-600 focus-within:border-primary-600'
        )}
      >
        <textarea
          id={id}
          type="text"
          name="name"
          className={cn(
            'block w-full border-0 p-0 text-gray-900 placeholder-gray-300 focus:ring-0 sm:text-sm',
            'focus:outline-none',
            'pr-10' // with icon
          )}
          rows="5"
          onChange={handleChange}
          value={value || ''}
          maxLength={maxLength}
          {...props}
        />
        {withCounter && maxLength && (
          <small className="absolute text-xs text-gray-500 right-6 bottom-3">
            {value.length}/{maxLength}
          </small>
        )}
        {Icon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
        )}
      </div>
    </div>
  );
}

FormTextArea.defaultProps = {
  id: '',
  icon: false,
  type: 'text',
  error: null,
  required: false,
  onChange: false,
  onSetFieldValue: false,
  maxLength: null,
  withCounter: false,
};

FormTextArea.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.bool]),
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  maxLength: PropTypes.number,
  withCounter: PropTypes.bool,
};

export default React.memo(FormTextArea);
